/**
 * runidea.ai — Phase 12 — Single-line full-address parser.
 *
 * The wizard's Step 1 used to have 4 separate inputs (street/city/state/zip).
 * We replaced those with a single full-address field. This module turns the
 * raw string into structured parts that the property-lookup API can consume.
 *
 * It is intentionally regex-based and dependency-free — Google Places
 * resolves the same shape when available, but we need a robust fallback for
 * the No-Places-key case.
 *
 * Accepts shapes like:
 *    "8335 SW 72nd Ave Apt 205d, Miami, FL 33143"
 *    "123 Main St, Springfield, IL 62704"
 *    "500 Boylston St #4B, Boston MA 02116"
 *    "1600 Amphitheatre Pkwy, Mountain View, CA 94043-1351"
 *    "9143 SW 77th Ave Apt B802 Miami FL 33173"            // no commas
 */

// US state abbreviations + full names so we can recognize "Florida" too.
const US_STATES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas",
  CA: "California", CO: "Colorado", CT: "Connecticut", DE: "Delaware",
  DC: "District of Columbia",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan",
  MN: "Minnesota", MS: "Mississippi", MO: "Missouri", MT: "Montana",
  NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota",
  OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania",
  RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota", TN: "Tennessee",
  TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia", WA: "Washington",
  WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};
const STATE_ABBRS = Object.keys(US_STATES);
const STATE_NAME_TO_ABBR = Object.fromEntries(
  Object.entries(US_STATES).map(([abbr, name]) => [name.toLowerCase(), abbr])
);

const UNIT_RE = /(?:\b(apt|apartment|unit|suite|ste|no\.?|num)\s*\.?\s*([A-Za-z0-9-]+)|#\s*([A-Za-z0-9-]+))\b/i;

// US ZIP detection — Phase 41 fix.
//
// Original bug: the prior regex `/\b(\d{5})(?:-?\d{4})?\b/` matched the
// FIRST 5-digit run in the string, so an address starting with a 5+ digit
// street number (e.g. "10748 N Kendall Dr Apt G1, Miami, FL 33176") had
// its street number captured as the ZIP, triggering a spurious "ZIP
// doesn't match the address" warning AND rendering the subtitle as
// "Miami, 10748 · Apt G1".
//
// Strategy (preference order, no loose fallback):
//   1. CANONICAL_ZIP_RE — the canonical US tail "STATE 5-digit ZIP"
//      (e.g. ", FL 33176" or " FL 33176-1234"). Highest-confidence
//      signal a ZIP is present, regardless of what other 5-digit runs
//      sit earlier in the string.
//   2. TRAILING_ZIP_RE — a 5-digit ZIP anchored to end-of-string with
//      optional trailing whitespace. Covers "9143 ... 33173" (no
//      state token) and PO-Box-style addresses.
//
// If BOTH miss, we set zip=null. The user spec requires "Address with
// NO ZIP → returns null ZIP (don't fabricate)" — a free-floating
// 5-digit run in the middle of the address (e.g. an apt number or
// street number) is NOT a reliable ZIP signal and was the source of
// the original mis-parse.
const CANONICAL_ZIP_RE = /\b([A-Z]{2})\s+(\d{5})(?:-?(\d{4}))?\b/i;
const TRAILING_ZIP_RE = /\b(\d{5})(?:-?(\d{4}))?\s*$/;

/**
 * @param {string} raw
 * @returns {{
 *   street: string|null,
 *   unit: string|null,
 *   city: string|null,
 *   state: string|null,        // 2-letter abbreviation
 *   zip: string|null,          // 5-digit
 *   ok: boolean,               // true if at least state OR zip resolved
 *   confidence: "high"|"medium"|"low",
 *   raw: string,
 * }}
 */
export function parseAddress(raw) {
  const result = {
    street: null, unit: null, city: null, state: null, zip: null,
    ok: false, confidence: "low", raw: raw == null ? "" : String(raw),
  };
  if (!raw || typeof raw !== "string") return result;
  let s = raw.trim().replace(/\s+/g, " ");
  if (!s) return result;

  // 1) Extract zip. Try strategies in order of confidence (NO loose
  //    fallback — per Phase 41 spec, "Address with NO ZIP → returns
  //    null ZIP (don't fabricate)"):
  //    a. Canonical "STATE 5-digit ZIP" tail  →  highest confidence
  //    b. End-of-string 5-digit run            →  high confidence
  //
  // This replaces the pre-Phase-41 "first \d{5}" scan, which would grab
  // the leading street number on addresses like
  // "10748 N Kendall Dr Apt G1, Miami, FL 33176" and treat it as a ZIP.
  let zipStart = -1;
  let zipEnd = -1;
  const canonical = s.match(CANONICAL_ZIP_RE);
  if (canonical && typeof canonical.index === "number") {
    result.zip = canonical[2];
    // Slice out only the ZIP (and any +4 extension), leaving the state token
    // in place so downstream comma/state parsing still resolves the state.
    const zipFull = canonical[3] ? `${canonical[2]}-${canonical[3]}` : canonical[2];
    const zipIndexInMatch = canonical[0].indexOf(canonical[2]);
    zipStart = canonical.index + zipIndexInMatch;
    zipEnd = zipStart + zipFull.length;
  } else {
    const tail = s.match(TRAILING_ZIP_RE);
    if (tail && typeof tail.index === "number") {
      result.zip = tail[1];
      zipStart = tail.index;
      zipEnd = zipStart + tail[0].length;
    }
    // No `else` — if neither matches, result.zip stays null.
  }
  if (zipStart >= 0) {
    s = (s.slice(0, zipStart) + s.slice(zipEnd))
      .trim()
      .replace(/,\s*$/, "")
      .replace(/\s+/g, " ");
  }

  // 2) Extract unit.
  const unitMatch = s.match(UNIT_RE);
  if (unitMatch) {
    // Branch 1 was the keyword form (Apt/Unit/...); Branch 2 was the # form.
    if (unitMatch[1]) {
      result.unit = `${capitalizeUnitWord(unitMatch[1])} ${unitMatch[2]}`.trim();
    } else if (unitMatch[3]) {
      result.unit = `# ${unitMatch[3]}`;
    }
    // Remove the unit phrase from the string so it doesn't bleed into city.
    s = (s.slice(0, unitMatch.index) + s.slice(unitMatch.index + unitMatch[0].length)).trim();
    s = s.replace(/\s+,/g, ",").replace(/,\s*$/, "");
  }

  // 3) Split by commas (preferred) — last token is state, second-to-last is city.
  const parts = s.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 3) {
    result.street = parts[0] || null;
    result.city = parts[1] || null;
    // Last part may be "FL" or "Florida" or "FL 33143" already-stripped.
    const last = parts[parts.length - 1];
    const abbr = resolveStateFromToken(last);
    if (abbr) result.state = abbr;
  } else if (parts.length === 2) {
    // "Street, City State" — try splitting last by spaces.
    result.street = parts[0] || null;
    const tail = parts[1];
    const tailTokens = tail.split(/\s+/);
    const lastTok = tailTokens[tailTokens.length - 1];
    const abbr = resolveStateFromToken(lastTok);
    if (abbr) {
      result.state = abbr;
      result.city = tailTokens.slice(0, -1).join(" ") || null;
    } else {
      // Maybe state is two-word like "New York"; try last two tokens.
      const twoTok = tailTokens.slice(-2).join(" ");
      const abbr2 = resolveStateFromToken(twoTok);
      if (abbr2) {
        result.state = abbr2;
        result.city = tailTokens.slice(0, -2).join(" ") || null;
      } else {
        result.city = tail || null;
      }
    }
  } else if (parts.length === 1) {
    // No commas — try to detect state token from the right and split there.
    const tokens = parts[0].split(/\s+/);
    let stateIdx = -1;
    for (let i = tokens.length - 1; i >= 0; i--) {
      if (resolveStateFromToken(tokens[i])) { stateIdx = i; break; }
    }
    if (stateIdx > 0) {
      result.state = resolveStateFromToken(tokens[stateIdx]);
      // City = tokens between street and state. Heuristic: city is typically
      // the 1-2 tokens just before state. We pick everything between an
      // address-number boundary and the state.
      const beforeState = tokens.slice(0, stateIdx);
      // Find the last "street suffix" or fall back to splitting after the
      // first 2 tokens.
      const streetSuffixIdx = findLastStreetSuffix(beforeState);
      if (streetSuffixIdx >= 0) {
        result.street = beforeState.slice(0, streetSuffixIdx + 1).join(" ");
        result.city = beforeState.slice(streetSuffixIdx + 1).join(" ") || null;
      } else {
        // No suffix detected — split halfway as a last resort.
        const split = Math.max(1, Math.floor(beforeState.length / 2));
        result.street = beforeState.slice(0, split).join(" ");
        result.city = beforeState.slice(split).join(" ") || null;
      }
    } else {
      result.street = parts[0];
    }
  }

  // Trim trailing comma artefacts and collapse internal whitespace.
  ["street", "city", "state", "unit", "zip"].forEach((k) => {
    if (typeof result[k] === "string") result[k] = result[k].replace(/^\s*,?\s*|\s*,?\s*$/g, "").trim() || null;
  });

  // Confidence + ok.
  const have = (k) => !!result[k];
  const score = (have("street") ? 1 : 0) + (have("city") ? 1 : 0) + (have("state") ? 1 : 0) + (have("zip") ? 1 : 0);
  result.ok = score >= 2 && (have("state") || have("zip"));
  result.confidence = score >= 4 ? "high" : score >= 3 ? "medium" : score >= 2 ? "low" : "low";
  return result;
}

function capitalizeUnitWord(w) {
  const m = (w || "").toLowerCase();
  if (m === "#") return "#";
  if (m === "ste") return "Ste";
  if (m === "apt" || m === "apartment") return "Apt";
  if (m === "unit") return "Unit";
  if (m === "suite") return "Suite";
  if (m === "no" || m === "no.") return "No.";
  if (m === "num") return "Num";
  return w;
}

function resolveStateFromToken(tok) {
  if (!tok) return null;
  const cleaned = tok.replace(/[.,]/g, "").trim();
  const upper = cleaned.toUpperCase();
  if (STATE_ABBRS.includes(upper)) return upper;
  const lower = cleaned.toLowerCase();
  if (STATE_NAME_TO_ABBR[lower]) return STATE_NAME_TO_ABBR[lower];
  return null;
}

// Common US street suffix words. The presence of any of these in the
// "before state" tokens is a strong hint that the street ends here.
const STREET_SUFFIXES = new Set([
  "st", "street", "ave", "avenue", "blvd", "boulevard", "rd", "road",
  "dr", "drive", "ln", "lane", "ct", "court", "way", "pkwy", "parkway",
  "ter", "terrace", "pl", "place", "trl", "trail", "cir", "circle",
  "hwy", "highway", "loop", "alley",
]);

function findLastStreetSuffix(tokens) {
  for (let i = tokens.length - 1; i >= 0; i--) {
    const t = (tokens[i] || "").toLowerCase().replace(/[.,]/g, "");
    if (STREET_SUFFIXES.has(t)) return i;
  }
  return -1;
}

/**
 * Re-formats parsed parts back into a canonical "Street Unit, City, ST ZIP"
 * string. Used to render the parsed subtitle below the wizard input.
 */
export function formatParsedSubtitle(parsed) {
  if (!parsed) return "";
  const tail = [parsed.city, parsed.state, parsed.zip].filter(Boolean).join(parsed.state ? " " : ", ").trim();
  const city = parsed.city || "";
  const tailStr = [city, [parsed.state, parsed.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const unit = parsed.unit ? ` · ${parsed.unit}` : "";
  return `${tailStr}${unit}`.trim();
}

export default parseAddress;
